import React, { useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Skeleton,
} from '@mui/material';
import { ProductCard } from '../ProductCard/ProductCard';
import { Producto } from '../../types';

interface ProductGridProps {
  productos: Producto[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onEdit: (producto: Producto) => void;
  onDelete: (id: number) => void;
  onViewDetails: (producto: Producto) => void;
  onLoadMore: () => void;
}

export function ProductGrid({
  productos,
  loading,
  loadingMore,
  hasMore,
  onEdit,
  onDelete,
  onViewDetails,
  onLoadMore,
}: ProductGridProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Setup IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasMore &&
          !loadingMore &&
          !loading
        ) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, loadingMore, loading, onLoadMore]);

  // Show skeleton cards during initial loading
  if (loading && productos.length === 0) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 4,
        }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={400}
            sx={{
              borderRadius: 1,
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: `${i * 50}ms`,
              '@keyframes pulse': {
                '0%': {
                  opacity: 0.6,
                },
                '50%': {
                  opacity: 1,
                },
                '100%': {
                  opacity: 0.6,
                },
              },
            }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Product Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 4,
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(8px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        {productos.map((producto, index) => (
          <Box
            key={producto.id}
            sx={{
              animation: 'fadeIn 0.4s ease-in-out',
              animationDelay: `${index * 40}ms`,
            }}
          >
            <ProductCard
              producto={producto}
              onEdit={onEdit}
              onDelete={onDelete}
              onViewDetails={onViewDetails}
            />
          </Box>
        ))}
      </Box>

      {/* Sentinel for infinite scroll */}
      <Box
        ref={sentinelRef}
        sx={{
          mt: 6,
          textAlign: 'center',
          minHeight: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loadingMore && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              animation: 'fadeIn 0.3s ease-in-out',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                },
                '100%': {
                  opacity: 1,
                },
              },
            }}
          >
            <CircularProgress size={44} thickness={4} />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              Cargando más productos...
            </Typography>
          </Box>
        )}
        {!hasMore && productos.length > 0 && (
          <Typography
            color="textSecondary"
            variant="body2"
            sx={{
              animation: 'fadeIn 0.3s ease-in-out',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                },
                '100%': {
                  opacity: 1,
                },
              },
            }}
          >
            ✓ No hay más productos
          </Typography>
        )}
      </Box>
    </Box>
  );
}
